import { widgetViewAccess } from "@salestrak/widgets";

/**
 * The pure client half of the widget view-ownership decision: grammar access
 * (the SAME shared module the server guard feeds) plus the UX policy layered
 * on top. Pure and sync so the node test harness can truth-table it;
 * `useWidgetViewAccess` resolves the actor inputs from client data and calls
 * this.
 *
 * ONE model for every view: a view has a shared/default board, and any viewer
 * may hold a personal fork of it (a per-user settings snapshot — the server
 * never sees it as widget writes, so it needs no permission). The only
 * permission question left is who may edit the SHARED board: `canWrite`.
 */

export type WidgetViewTier =
  /** Own `member:` view (My Work) — full direct edit, no permission needed,
   *  no fork (the view already IS personal). */
  | "private-owner"
  /** Any shared view. `canWrite` says whether this viewer may edit the shared
   *  board; personalization is open to every viewer. */
  | "shared";

export type WidgetViewPolicyActor = {
  userId: string;
  /** Holds the `widgets.manage` permission. */
  canManageWidgets: boolean;
  /** Leads the given team — the shared-write grant (team scope only). */
  leadsTeam: (teamId: string) => boolean;
};

/** WHICH grant made the viewer a shared-board editor — copy-level context so
 *  the UI can say "because you lead this team" instead of a bare "you can
 *  edit". `team-lead` wins when both apply (the more personal reason). Null
 *  when the viewer can't write shared rows (or owns a private view — no
 *  grant concept there). */
export type WidgetViewWriteGrant = "team-lead" | "permission";

export type WidgetViewPolicy = {
  /** Whether writes to the canonical board will be accepted by the server. */
  canWrite: boolean;
  writeGrant: WidgetViewWriteGrant | null;
  tier: WidgetViewTier;
};

export function widgetViewPolicy(
  view: string,
  actor: WidgetViewPolicyActor,
): WidgetViewPolicy {
  // The privacy bypass is server-only (no UI reaches a foreign private
  // view), so it's always false here.
  const { canWrite, ownership } = widgetViewAccess(view, {
    userId: actor.userId,
    canManageWidgets: actor.canManageWidgets,
    leadsTeam: actor.leadsTeam,
    privacyBypass: false,
  });

  if (ownership.visibility === "private") {
    // The UI only ever renders the current user's own member view, so
    // `private-owner` is the only reachable private tier; a non-owner
    // private view (canWrite false) degrades to read-only shared just in
    // case.
    return canWrite
      ? { canWrite, writeGrant: null, tier: "private-owner" }
      : { canWrite, writeGrant: null, tier: "shared" };
  }
  if (canWrite) {
    const writeGrant =
      ownership.scope?.kind === "team" && actor.leadsTeam(ownership.scope.entityId)
        ? "team-lead"
        : "permission";
    return { canWrite, writeGrant, tier: "shared" };
  }
  return { canWrite, writeGrant: null, tier: "shared" };
}
